import { useSearchParams } from 'react-router-dom'
import { compact } from 'lodash'
import { useEffect, useState } from 'react'
import { MarketingContactTable } from '../shared/MarketingContactTable'
import ScheduleRateGraph from './ScheduleRateGraph'
import SummaryTab from './SummaryTab'
import { convertUrlFiltersToES, PredefinedFilter } from 'app/filter.utils'
import { gqlQuery } from 'app/lib/graphqlClient'
import { showModal } from 'app/lib/modal'
import { formatPercent } from 'app/lib/utils'
import type { ResultOf } from '@graphql-typed-document-node/core'
import { useHandleSearchParams } from 'app/utils'
import { StatsCardType } from 'types/StatsCardType'
import { graphql } from 'app/generated/gql'

const QUALIFIED_LEAD_COUNTS_QUERY = graphql(`
  query ScheduleRateQualifiedLeadCounts(
    $dateRange: DateRangeInput!
    $compareFromDate: String
    $compareToDate: String
    $channel: String
    $locationIds: [String!]
    $activityType: String
    $integrationStatus: String
    $filters: String
    $platform: String
  ) {
    qualifiedLeadCounts(
      dateRange: $dateRange
      compareFromDate: $compareFromDate
      compareToDate: $compareToDate
      channel: $channel
      locationIds: $locationIds
      activityType: $activityType
      integrationStatus: $integrationStatus
      filters: $filters
      platform: $platform
    ) {
      total
      qualified
      disqualified
      stats {
        scheduleRate
        mqls
        scheduled
        notScheduled
      }
      comparisonStats {
        scheduleRate
        mqls
        scheduled
        notScheduled
      }
      series {
        date
        scheduled
        leads
        scheduledRate
      }
    }
  }
`)

type ScheduleRateMetrics = ResultOf<typeof QUALIFIED_LEAD_COUNTS_QUERY>[`qualifiedLeadCounts`]

export default function ScheduleRateSection({
  channel,
  fromDate,
  toDate,
  compareFromDate,
  compareToDate,
  locationIds,
  integrationStatus,
  dontCompare,
  filters,
  platform,
  defaultFilters,
}: {
  channel: string
  fromDate: string
  toDate: string
  compareFromDate: string
  compareToDate: string
  locationIds: string[]
  integrationStatus?: string
  dontCompare: boolean
  filters?: string
  platform?: string
  defaultFilters?: PredefinedFilter
}) {
  const [metrics, setMetrics] = useState<ScheduleRateMetrics>()
  const [searchParams] = useSearchParams()
  const handleSearchParams = useHandleSearchParams()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [stats, setStats] = useState<StatsCardType[]>()
  const [filterByActivityType, setFilterByActivityType] = useState<string>(`All`)
  const [filterContactsBy, setFilterContactsBy] = useState(searchParams.get(`details`))
  const [campaign, setCampaign] = useState<string[] | undefined>()

  const handleCloseModal = () => setFilterContactsBy(null)

  useEffect(() => {
    if (!fromDate || !toDate) return
    setIsLoading(true)
    gqlQuery(QUALIFIED_LEAD_COUNTS_QUERY, {
      dateRange: { fromDate, toDate },
      compareFromDate,
      compareToDate,
      channel,
      locationIds: locationIds.length ? locationIds : undefined,
      activityType: filterByActivityType !== `All` ? filterByActivityType : undefined,
      integrationStatus: integrationStatus || undefined,
      filters: filters || undefined,
      platform: platform || undefined,
    }).then((result) => {
      setIsLoading(false)
      if (result.data?.qualifiedLeadCounts) {
        setMetrics(result.data.qualifiedLeadCounts)
      }
    })
    if (filters) {
      setCampaign(convertUrlFiltersToES(filters))
    } else setCampaign(undefined)
  }, [
    fromDate,
    toDate,
    channel,
    locationIds,
    compareFromDate,
    compareToDate,
    filterByActivityType,
    integrationStatus,
    filters,
    platform,
  ])

  useEffect(() => {
    if (metrics) {
      setStats([
        {
          label: `Schedule Rate`,
          value: `${formatPercent(metrics.stats?.scheduleRate)}%`,
          trend:
            !dontCompare && metrics.comparisonStats
              ? {
                  previous: metrics.comparisonStats.scheduleRate,
                  current: metrics.stats?.scheduleRate,
                }
              : undefined,
          infoTooltip: {
            title: `Schedule Rate`,
            message: `Percentage of qualified leads that scheduled a first appointment within the date range.`,
          },
        },
        {
          label: `Qualified Leads`,
          value: `${metrics.stats?.mqls?.toLocaleString(`en-US`)}`,
          trend:
            !dontCompare && metrics.comparisonStats
              ? {
                  previous: metrics.comparisonStats.mqls,
                  current: metrics.stats?.mqls,
                }
              : undefined,
          // oxlint-disable-next-line react/immutability -- existing closure or mutable API pattern is intentional
          onOpenClick: () => openModal(`Qualified Leads`, `Qualified Leads`),
          infoTooltip: {
            title: `Qualified Leads`,
            message: `All contacts with a became a lead date within the date range who also do not have a disqualifying closed lost reason.`,
          },
        },
        {
          label: `Scheduled`,
          value: `${metrics.stats?.scheduled?.toLocaleString(`en-US`)}`,
          trend:
            !dontCompare && metrics.comparisonStats
              ? {
                  previous: metrics.comparisonStats.scheduled,
                  current: metrics.stats?.scheduled,
                }
              : undefined,
          onOpenClick: () => openModal(`Scheduled Qualified Leads`, `Scheduled`),
          infoTooltip: {
            title: `Scheduled`,
            message: `All qualified contacts with a became a lead date within the date range and have scheduled a first appointment.`,
          },
        },
        {
          label: `Not Scheduled`,
          value: `${metrics.stats?.notScheduled?.toLocaleString(`en-US`)}`,
          trend:
            !dontCompare && metrics.comparisonStats
              ? {
                  previous: metrics.comparisonStats.notScheduled,
                  current: metrics.stats?.notScheduled,
                }
              : undefined,
          onOpenClick: () => openModal(`Not Scheduled Qualified Leads`, `Not Scheduled`),
          infoTooltip: {
            title: `Not Scheduled`,
            message: `All qualified contacts with a became a lead date within the date range and have not scheduled a first appointment.`,
          },
        },
      ])
    }
  }, [metrics])

  useEffect(() => {
    let currentParam = searchParams.get(`details`)
    if (filterContactsBy === currentParam) return
    handleSearchParams({
      details: filterContactsBy ?? undefined,
      ...(searchParams.has(`filters`) ? { filters: undefined } : undefined),
    })
  }, [filterContactsBy])

  useEffect(() => {
    if (
      filterContactsBy &&
      [`Qualified Leads`, `Scheduled Qualified Leads`, `Not Scheduled Qualified Leads`].includes(filterContactsBy) &&
      fromDate &&
      toDate
    ) {
      const title =
        filterContactsBy === `Qualified Leads`
          ? `Qualified Leads`
          : filterContactsBy === `Scheduled Qualified Leads`
            ? `Scheduled`
            : `Not Scheduled`
      showModal(
        <MarketingContactTable
          title={title}
          channel={channel}
          filterContactsBy={filterContactsBy}
          fromDate={fromDate}
          toDate={toDate}
          locationIds={locationIds}
          integrationStatus={integrationStatus}
          filterByFirstActivityType={filterByActivityType}
          campaign={campaign}
          platform={platform}
          onClose={handleCloseModal}
          defaultFilters={defaultFilters}
        />,
        {
          hideTitle: true,
          removePadding: true,
          isFullScreen: true,
          onClose: handleCloseModal,
          useRadix: true,
        },
      )
    }
  }, [filterContactsBy, fromDate, toDate])

  const openModal = (type: string, title: string) => {
    setFilterContactsBy(type)
  }

  return (
    <>
      <SummaryTab
        title="Schedule Rate"
        hideChartOnMobile
        graph={
          <ScheduleRateGraph
            metrics={metrics}
            isLoading={isLoading}
            onFilterByActivityTypeChange={setFilterByActivityType}
          />
        }
        statsCards={stats ?? []}
      />
    </>
  )
}
